import { format } from 'svelte-i18n';
import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
import { compareFWVersions } from 'src/helpers/firmware_compare_helpers';
import { isFwAfter200 } from 'src/helpers/ble_devices_helpers';
import { getTRVFirmwareVersion } from 'src/helpers/firmware_helpers';
import {
	is_GBLE,
	is_gen_2,
	is_loqed,
	isBluTRV,
	isWallDisplay,
	real_id,
} from 'src/helpers/interface_helpers';
import { firmwares_gen_gble } from 'src/stores/device-runtime/process';
import { cachedLists, for_update } from 'src/stores/device-runtime/firmware-ui';
import type { Device } from 'src/types/Device';
import { get } from 'svelte/store';
import { deviceMainFilter } from 'src/stores/ui/filters';
import { isGatewayObserverEnabled } from 'src/helpers/ble_devices_helpers';
import { hasMappedRemoteControls } from 'src/helpers/bthome_remote_status_helper';
import type { BuildRawStatusIconOptions } from 'src/types/StatusIcons';

export interface RawStatusIcon {
	show: boolean;
	icon: string;
	active?: boolean;
	iconColor?: string;
	text?: string;
}

export const buildRawStatusIcon = (
	device: Device,
	status: any,
	settings: any,
	context: 'drawer' | 'card',
	options?: BuildRawStatusIconOptions,
): RawStatusIcon[] => {
	if (!device) return [];

	const id = device.id;
	const realId = real_id(id);
	const virtualDevice = device?.category === 'virtual_device';
	const peripheralDevice = isPeripheralDevice(device?.type);
	const zendureDevice = device?.category === 'pv';
	const bluTRVDevice = isBluTRV(device?.type);
	const loqedDevice = is_loqed(device);
	const gbleDevice = is_GBLE(device);

	const inDrawer = context === 'drawer';
	const isOnline = !!status && !!settings && status?.online;
	const hasCloud = settings?.cloud?.enabled || settings?.cloud?.enable || zendureDevice || loqedDevice;
	const isGen2 = is_gen_2(device) && !bluTRVDevice;
	const versionInStatus = bluTRVDevice
		? getTRVFirmwareVersion(device)
		: status?.['fwversion:0']?.version || '1.0.0';
	const latestFirmwareVersion =
		gbleDevice || bluTRVDevice
			? get(firmwares_gen_gble)?.[device?.type]?.version
			: get(for_update)[realId]?.newFwVersion;
	const hasUpdate =
		(typeof latestFirmwareVersion !== 'undefined' &&
			compareFWVersions(versionInStatus, '<', latestFirmwareVersion)) ||
		(bluTRVDevice
			? typeof get(for_update)?.[id] !== 'undefined'
			: typeof get(for_update)?.[realId] !== 'undefined');

	const wifiEnabled = status?.wifi_sta?.connected || status?.wifi?.status === 'got ip';
	const ethernetEnabled = status?.eth?.ip;
	const bleEnabled = isFwAfter200(settings) ? settings?.ble?.rpc?.enable : settings?.ble?.enable;
	const rangeExteder = settings?.wifi?.ap?.range_extender?.enable;
	const connectedDevices = status?.wifi?.ap_client_count;
	const apEnabled = settings?.wifi?.ap?.enable;
	const mqttEnabled = status?.mqtt?.connected;
	const isBleGateway =
		isGen2 &&
		bleEnabled &&
		!virtualDevice &&
		!peripheralDevice &&
		isGatewayObserverEnabled(device) &&
		get(cachedLists)?.[device.id]?.['ble.cloudrelay.list']?.addrs?.length > 0;

	const hasMappedRemotes =
		typeof options?.hasMappedRemotesOverride === 'boolean'
			? options.hasMappedRemotesOverride
			: hasMappedRemoteControls(device, status, settings);

	const icons: RawStatusIcon[] = [
		{
			show: inDrawer && hasMappedRemotes,
			icon: 'fas fa-gamepad',
			active: true,
		},
		{
			show: inDrawer || (!zendureDevice && !loqedDevice),
			icon: 'fas fa-share-nodes',
			active: !!device?.shared || !!device?.shared_by_me,
		},
		{
			show: !gbleDevice,
			icon: 'fas fa-cloud',
			active: isOnline && hasCloud,
		},
		{
			show: gbleDevice,
			icon: 'fas fa-lock',
			active: device?.encrypted,
		},
		{
			show: inDrawer || (!zendureDevice && !loqedDevice),
			icon: 'fas fa-sync',
			active: isOnline && hasUpdate,
		},
		{
			show: (inDrawer || apEnabled) && isGen2 && !isWallDisplay(device.type),
			icon: 'fas fa-signal-stream',
			active: isOnline && apEnabled,
		},
		{
			show: inDrawer && isGen2,
			icon: 'fas fa-wifi',
			active: isOnline && wifiEnabled,
		},
		{
			show: inDrawer && isGen2,
			icon: 'fas fa-bluetooth',
			active: isOnline && bleEnabled,
		},
		{
			show: inDrawer && isGen2 && /SPS|SPE|SPD/.test(device?.type),
			icon: 'fas fa-network-wired',
			active: isOnline && ethernetEnabled,
		},
		{
			show: (inDrawer || mqttEnabled) && isGen2,
			active: isOnline && mqttEnabled,
			icon: 'mqtt',
		},
		{
			show: !inDrawer && hasMappedRemotes,
			icon: 'fas fa-gamepad',
			active: true,
		},
		{
			show: !inDrawer && isBleGateway,
			icon: 'fas fa-eye',
			iconColor: 'primary',
			text: get(format)('card_status_bar_ble_observer', {
				default: 'BLE Observer',
			}),
		},
		{
			show: !inDrawer && isGen2 && rangeExteder && !virtualDevice && !peripheralDevice,
			icon: 'fas fa-router',
			iconColor: 'success',
			text:
				typeof connectedDevices !== 'undefined'
					? `${connectedDevices} ${get(format)('card_status_bar_device_connected', {
							default: 'device connected',
						})}`
					: '',
		},
		{
			show: !inDrawer && virtualDevice,
			icon: 'far fa-cube',
			iconColor: 'success',
			text: get(format)('label_virtual_device', {
				default: 'Virtual Device',
			}),
		},
		{
			show: !inDrawer && peripheralDevice,
			icon: 'far fa-puzzle-piece',
			iconColor: 'warning',
			text: get(format)('label_peripheral_device', {
				default: 'Peripheral Device',
			}),
		},
		{
			show: !inDrawer && !!get(deviceMainFilter),
			icon: 'fa-regular fa-check',
			iconColor: 'secondary',
			text: id,
		},
		{
			show: !inDrawer && !!get(deviceMainFilter),
			icon: 'fa-regular fa-check',
			iconColor: 'secondary',
			text: device?.type,
		},
	];

	return icons.filter((i) => i.show);
};
